/**
 * @file        src/App.jsx
 * @description Router shell. Mounts the AppLayout (right sidebar from design)
 *              and lazy-loads each view as its own route chunk (route-level
 *              code splitting per template §4.1).
 * @author      Kinetika Labs
 * @created     2026-06-22
 * @changelog
 *   - 2026-10-28 Claude — Removed the /trip-planner route + its lazy import, as Neon
 *                          did on 2026-08-03 (client: feature dropped). The page was
 *                          an unfinished mock-up with no menu link, and its Sign in
 *                          button did nothing. pages/TripPlanner/ and
 *                          data/tripPlanner.js are deleted; the path now falls
 *                          through to the catch-all.
 *   - 2026-10-23 Claude — /amendment/hotel-cancel/:ref → pages/CancelHotel (hotel Cancellation page).
 *   - 2026-11-03 Claude — /amendment/void/:ref → pages/VoidBooking (Void page, with the
 *                          flight + passenger grid TripJack's own screen shows).
 *   - 2026-10-15 Claude — /amendment/cancel/:ref → pages/CancelFlight (Cancellation page, §43).
 *   - 2026-10-13 Claude — /amendment/full-refund/:ref → pages/FullRefund (Full Refund page).
 *   - 2026-10-09 Claude — SEO Management: mount <SeoHead /> once beside
 *                          <FaviconManager /> (one global head manager keyed on
 *                          the path), and the inner catch-all now renders a real
 *                          NotFound page instead of redirecting every unknown
 *                          URL to home (a soft 404). Legacy redirects unchanged.
 *   - 2026-10-01 Claude — HOLDBOOK: /hotels/hold/:ref/review — the hold Review page (Confirm & Pay).
 *   - 2026-09-11 Claude — HOLDBOOK: /hotels/hold/:ref (lazy) — a room held without
 *                          payment, with its deadline and Confirm & Pay.
 *   - 2026-08-04 Claude — /packages/:idOrSlug detail route (CR A1): package cards
 *                          now open a dedicated page instead of the in-place
 *                          PackageDetailModal.
 *   - 2026-07-27 Claude — Added company/footer pages: /about, /faqs, /testimonial and
 *                          /contact (content mirrored from beyonddestination.in, themed
 *                          to the app). Footer "About" column now links to them.
 *   - 2026-07-20 Claude — Legal routes lifted OUT of the app shell: /legal/terms
 *                          and /legal/privacy now render standalone (no sidebar,
 *                          no modals host) while everything else mounts under a
 *                          catch-all <AppShell>. Legal links open in a new tab.
 *   - 2026-07-20 Claude — Per-product privacy pages replaced by one /legal/privacy
 *                          document; the old hotel/flight privacy URLs now redirect
 *                          to it.
 *   - 2026-07-19 Claude — Per-product terms pages replaced by one /legal/terms
 *                          document; the old hotel/flight/insurance terms URLs now
 *                          redirect to its matching section.
 *   - 2026-06-22 Claude — Initial.
 *   - 2026-06-22 Claude — Removed FlightHome page/route; the sidebar "Flights"
 *                          entry now opens Home with its Flights search tab.
 *   - 2026-06-22 Claude — Mount <ModalsHost /> (was imported but never
 *                          rendered, so NO modal opened — "+ More Fares", auth,
 *                          baggage, etc. all dispatched events into the void).
 *   - 2026-06-22 Claude — Removed HotelHome page/route; the sidebar "Hotels"
 *                          entry now opens Home with its Hotels search tab
 *                          (mirrors the Flights change).
 *   - 2026-07-07 Claude — Blog routes: /blog (list) + /blog/:slug (detail),
 *                          fed by the admin-managed blogs (public read on :5000).
 *   - 2026-08-03 Claude — Mount <NavStackRecorder />: it records the path at each
 *                          history index so the funnel Back buttons can pop history
 *                          instead of pushing the previous step (hooks/useStepBack).
 *   - 2026-08-10 Claude — Mount <FaviconManager />: Beyond had no favicon
 *                          manager at all (unlike neon), so an admin-uploaded
 *                          favicon (brands.favicon_url) never took effect —
 *                          the tab icon was always the static /logo.png.
 */
import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import AppLayout from './layout/AppLayout.jsx';
import ModalsHost from './modals/ModalsHost.jsx';
import ToastHost from './components/ToastHost.jsx';
import NavStackRecorder from './components/NavStackRecorder.jsx';
import FaviconManager from './components/FaviconManager.jsx';
import SeoHead from './components/SeoHead.jsx';

const HomePage          = lazy(() => import('./pages/Home/index.jsx'));
const HotelResultsPage  = lazy(() => import('./pages/HotelResults/index.jsx'));
const HotelDetailPage   = lazy(() => import('./pages/HotelDetail/index.jsx'));
const HotelCheckoutPage = lazy(() => import('./pages/HotelCheckout/index.jsx'));
const HotelPaymentPage  = lazy(() => import('./pages/HotelPayment/index.jsx'));
const HotelHoldPage     = lazy(() => import('./pages/HotelHold/index.jsx')); // HOLDBOOK
const FlightResultsPage = lazy(() => import('./pages/FlightResults/index.jsx'));
const FlightBookPage    = lazy(() => import('./pages/FlightBook/index.jsx'));
const FlightReviewPage  = lazy(() => import('./pages/FlightReview/index.jsx'));
const FlightPaymentPage = lazy(() => import('./pages/FlightPayment/index.jsx'));
const ModifyBookingPage = lazy(() => import('./pages/ModifyBooking/index.jsx'));
const ReschedulePage    = lazy(() => import('./pages/Reschedule/index.jsx'));
const InsurancePage     = lazy(() => import('./pages/Insurance/index.jsx'));
const InsuranceResults  = lazy(() => import('./pages/Insurance/Results.jsx'));
const InsuranceBookPage = lazy(() => import('./pages/Insurance/Book.jsx'));
const PackagesPage      = lazy(() => import('./pages/Packages/index.jsx'));
const PackageDetailPage = lazy(() => import('./pages/PackageDetail/index.jsx'));
const BlogListPage      = lazy(() => import('./pages/Blog/index.jsx'));
const BlogDetailPage    = lazy(() => import('./pages/Blog/Detail.jsx'));
// Company pages (footer "About" column) — static content mirrored from
// beyonddestination.in, restyled to the app theme.
const AboutPage         = lazy(() => import('./pages/About/index.jsx'));
const FaqPage           = lazy(() => import('./pages/Faq/index.jsx'));
const TestimonialPage   = lazy(() => import('./pages/Testimonial/index.jsx'));
const ContactPage       = lazy(() => import('./pages/Contact/index.jsx'));
const ProfilePage       = lazy(() => import('./pages/Profile/index.jsx'));
const WalletPage        = lazy(() => import('./pages/Wallet/index.jsx'));
const ConfirmationPage  = lazy(() => import('./pages/Confirmation/index.jsx'));
const HotelVoucherPage  = lazy(() => import('./pages/Voucher/HotelVoucher.jsx'));
const FlightVoucherPage = lazy(() => import('./pages/Voucher/FlightVoucher.jsx'));
const InsuranceVoucherPage = lazy(() => import('./pages/Voucher/InsuranceVoucher.jsx'));
const HotelBookingDetailsPage = lazy(() => import('./pages/BookingDetails/HotelBookingDetails.jsx'));
const HotelStayDetailsPage = lazy(() => import('./pages/BookingDetails/HotelStayDetails.jsx'));
const FlightBookingDetailsPage = lazy(() => import('./pages/BookingDetails/FlightBookingDetails.jsx'));
const FlightCartDetailPage = lazy(() => import('./pages/BookingDetails/FlightCartDetail.jsx'));
const FullRefundPage = lazy(() => import('./pages/FullRefund/index.jsx'));
const CancelFlightPage = lazy(() => import('./pages/CancelFlight/index.jsx'));
const VoidBookingPage = lazy(() => import('./pages/VoidBooking/index.jsx'));
const CancelHotelPage = lazy(() => import('./pages/CancelHotel/index.jsx'));
const InsuranceBookingDetailsPage = lazy(() => import('./pages/BookingDetails/InsuranceBookingDetails.jsx'));
const InvoicePage       = lazy(() => import('./pages/Invoice/index.jsx'));
// One Terms & Conditions and one Privacy Policy cover every product. The old
// per-product pages are gone; their URLs redirect here so existing links work.
const TermsPage         = lazy(() => import('./pages/Legal/Terms.jsx'));
const PrivacyPage       = lazy(() => import('./pages/Legal/Privacy.jsx'));
const NotFoundPage      = lazy(() => import('./pages/NotFound/index.jsx'));

// Routes are code-split, so a refresh briefly waits on the page's JS chunk. That
// wait used to render the word "Loading…", which is the flash you see on every
// refresh. Render NOTHING instead: the chunk arrives in milliseconds and the page
// paints directly, with no text that appears only to be thrown away.
const Loader = () => null;

// Everything that lives inside the app chrome — the sidebar, the modals host,
// and every in-app route. Mounted under the catch-all in App() so the standalone
// legal documents can render OUTSIDE it.
function AppShell() {
  return (
    <AppLayout>
      <ModalsHost />
      {/* Outside <Routes> on purpose: a toast raised by the click that also
          navigates must outlive the page that raised it. */}
      <ToastHost />
      <Suspense fallback={<Loader />}>
        <Routes>
          <Route path="/"                element={<HomePage />} />
          <Route path="/hotels/results"  element={<HotelResultsPage />} />
          <Route path="/hotels/detail"   element={<HotelDetailPage />} />
          <Route path="/hotels/checkout" element={<HotelCheckoutPage />} />
          <Route path="/hotels/payment"  element={<HotelPaymentPage />} />
          {/* HOLDBOOK — a room held without payment; Confirm & Pay lives here. */}
          <Route path="/hotels/hold/:ref" element={<HotelHoldPage />} />
          {/* HOLDBOOK — Confirm & Pay on a hold: Review Your Booking (PAN, fare, pay). */}
          <Route path="/hotels/hold/:ref/review" element={<HotelHoldPage review />} />
          <Route path="/flights/results" element={<FlightResultsPage />} />
          <Route path="/flights/book"    element={<FlightBookPage />} />
          <Route path="/flights/review"  element={<FlightReviewPage />} />
          <Route path="/flights/payment" element={<FlightPaymentPage />} />
          {/* Post-booking add-ons (meal / baggage / seat) for a ticketed PNR. */}
          <Route path="/flights/modify"  element={<ModifyBookingPage />} />
          {/* Reschedule a ticketed PNR to a new flight (TripJack auto-reissue). */}
          <Route path="/flights/reschedule" element={<ReschedulePage />} />
          {/* The Flight+Hotel combo pages are removed — old links land on home. */}
          <Route path="/combo"           element={<Navigate to="/" replace />} />
          <Route path="/combo/results"   element={<Navigate to="/" replace />} />
          <Route path="/insurance"         element={<InsurancePage />} />
          <Route path="/insurance/results" element={<InsuranceResults />} />
          {/* Traveller details + Easebuzz handoff. The policy is bought server-side
              in the payment callback, so there is no separate /payment route. */}
          <Route path="/insurance/book"    element={<InsuranceBookPage />} />
          {/* Old static tour/cruise landings are gone — their URLs land on the
              LIVE admin-managed Packages page so old links keep working. */}
          <Route path="/tours/private"   element={<Navigate to="/packages" replace />} />
          <Route path="/tours/group"     element={<Navigate to="/packages" replace />} />
          <Route path="/cruises"         element={<Navigate to="/packages" replace />} />
          <Route path="/packages"        element={<PackagesPage />} />
          <Route path="/packages/:idOrSlug" element={<PackageDetailPage />} />
          <Route path="/blog"            element={<BlogListPage />} />
          <Route path="/blog/:slug"      element={<BlogDetailPage />} />
          {/* Company / footer pages */}
          <Route path="/about"           element={<AboutPage />} />
          <Route path="/faqs"            element={<FaqPage />} />
          <Route path="/testimonial"     element={<TestimonialPage />} />
          <Route path="/contact"         element={<ContactPage />} />
          <Route path="/profile"         element={<ProfilePage />} />
          <Route path="/wallet"          element={<WalletPage />} />
          <Route path="/bookings"        element={<Navigate to="/profile" replace state={{ tab: 'bookings' }} />} />
          <Route path="/confirmation"    element={<ConfirmationPage />} />
          <Route path="/voucher/hotel"   element={<HotelVoucherPage />} />
          <Route path="/voucher/flight"  element={<FlightVoucherPage />} />
          <Route path="/voucher/insurance" element={<InsuranceVoucherPage />} />
          <Route path="/booking/hotel"   element={<HotelBookingDetailsPage />} />
          {/* Guest-facing stay summary, opened from the cart view's More Options. */}
          <Route path="/hotel-booking-details/:ref" element={<HotelStayDetailsPage />} />
          <Route path="/booking/flight"  element={<FlightBookingDetailsPage />} />
          {/* Insurance success / booking details — same shape as flight & hotel. */}
          <Route path="/booking/insurance" element={<InsuranceBookingDetailsPage />} />
          {/* The booking reference IS the cart — mirrors the supplier's own
              /manage-carts/cart-detail/<ref>. One page; the Fare Summary and every
              amendment id on the booking both land here. */}
          <Route path="/cart-detail/:ref" element={<FlightCartDetailPage />} />
          {/* Full Refund (Book Amendments → Full Refund): every passenger + reason, then submit. */}
          <Route path="/amendment/full-refund/:ref" element={<FullRefundPage />} />
          <Route path="/amendment/cancel/:ref" element={<CancelFlightPage />} />
          <Route path="/amendment/void/:ref" element={<VoidBookingPage />} />
          <Route path="/amendment/hotel-cancel/:ref" element={<CancelHotelPage />} />
          <Route path="/invoice/:kind"   element={<InvoicePage />} />
          {/* A real 404, not a redirect to home (soft 404). SeoHead marks it noindex. */}
          <Route path="*"                element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </AppLayout>
  );
}

export default function App() {
  return (
    <Suspense fallback={<Loader />}>
      <FaviconManager />
      <SeoHead />
      <NavStackRecorder />
      <Routes>
        {/* Standalone legal documents — deliberately OUTSIDE <AppShell>, so they
            render with no sidebar and no modals host, filling the whole viewport.
            Every link to them opens a new tab, so a customer reading the terms
            never loses the booking they were part-way through. Two documents,
            each covering every product: general sections plus a per-product one. */}
        <Route path="/legal/terms"   element={<TermsPage />} />
        <Route path="/legal/privacy" element={<PrivacyPage />} />
        {/* Old per-product legal URLs — kept as redirects for links already in
            the wild (emails, vouchers, invoices) rather than 404ing them. Each
            lands on the matching section of the combined document. */}
        <Route path="/legal/hotel-terms"     element={<Navigate to="/legal/terms#hotel-and-accommodation-bookings" replace />} />
        <Route path="/legal/flight-terms"    element={<Navigate to="/legal/terms#flight-bookings" replace />} />
        <Route path="/legal/insurance-terms" element={<Navigate to="/legal/terms#travel-insurance" replace />} />
        <Route path="/legal/hotel-privacy"   element={<Navigate to="/legal/privacy#what-each-product-requires" replace />} />
        <Route path="/legal/flight-privacy"  element={<Navigate to="/legal/privacy#what-each-product-requires" replace />} />
        <Route path="/terms"   element={<Navigate to="/legal/terms" replace />} />
        <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />
        {/* Everything else renders inside the app chrome. */}
        <Route path="*" element={<AppShell />} />
      </Routes>
    </Suspense>
  );
}
